<template>
	<div>
		<!-- Top tabs -->
		<div class="flex xs-12 bb1 bc7 xs-right">
			<div
				v-for="(tab, i) in tabs"
				:key="i"
				@click="onSelect(tab)"
				class="cu2 fs3 p4"
				:class="tab.isSelected ? 'c2 fw7' : 'c6 bc0'"
			>
				{{ tab.label }}
			</div>
		</div>

		<!-- View -->
		<component
			:is="selectedView"
			@proposal:policies_created="handlePolicyCreation"
		/>

		<!-- Modal -->
		<core-modal name="confirmationModal" :headline="$t('share_policies')">
			<div class="p4">
				<!-- Number of policies message -->
				<div>
					{{
						shareForm.policiesIds.length > 1
							? $t("policies_created", {
									numberOfPolicies: shareForm.policiesIds.length
							  })
							: $t("policy_created")
					}}
				</div>
				<!-- share policies -->
				<div class="flex xs-middle xs-12 mt8">
					<j-base-toggle
						class="mr2"
						v-model="shareForm.toggleShare"
						size="xsmall"
					/>
					<div>
						{{ $t("shared_with_case_creator", { caseCreator: createdByName }) }}
					</div>
				</div>
				<!-- Send notifications -->
				<div class="flex xs-middle xs-12 mt4">
					<j-base-toggle
						class="mr2"
						v-model="shareForm.toggleSendNotification"
						size="xsmall"
					/>
					<div>{{ $t("send_notification") }}</div>
				</div>
				<!-- Error message -->
				<div class="xs-12 fs3 lh125 cError mt1" v-if="!policyCreatedTemplateId">
					{{ $t("notification_template_not_found") }}
				</div>

				<j-button class="mt6" :isLoading="isLoading" @click="onConfirm">{{
					$t("confirm")
				}}</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import ApeironForms from "./ApeironForms"
import CaseOverview from "./CaseOverview"
import CoreModal from "@/modules/core/components/CoreModal"
import JButton from "@/components/JButton"
import JBaseToggle from "@/components/JBaseToggle"
import CaseFiles from "./CaseFiles"
import CaseExplorer from "../CaseExplorer/CaseExplorer.vue"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		ApeironForms,
		CaseOverview,
		CoreModal,
		JButton,
		JBaseToggle,
		CaseFiles,
		CaseExplorer
	},

	computed: {
		createdByName() {
			return this.$store.state.caseManagement?.case?.createdBy?.username
		},
		createdById() {
			return this.$store.state.caseManagement?.case?.createdBy?.id
		}
	},

	data() {
		return {
			selectedView: null,
			isLoading: false,

			shareForm: {
				toggleShare: false,
				toggleSendNotification: false,
				proposalId: null,
				policiesIds: []
			},

			policyCreatedTemplateId: null,

			tabs: [
				{
					id: "CaseOverview",
					label: this.$t("overview"),
					isSelected: false
				},
				{
					id: "ApeironForms",
					label: this.$t("forms"),
					isSelected: false
				},
				{
					id: "CaseFiles",
					label: this.$t("files"),
					isSelected: false
				},
				{
					id: "CaseExplorer",
					label: this.$t("advanced_view"),
					isSelected: false
				}
			]
		}
	},

	methods: {
		onSelect(tab) {
			this.selectedView = tab?.id
			this.tabs = this.tabs.map(item => ({
				...item,
				isSelected: item.id === tab.id ? true : false
			}))
		},

		handlePolicyCreation({ proposalId, policiesIds }) {
			if (policiesIds?.length) {
				this.shareForm.proposalId = proposalId
				this.shareForm.policiesIds = policiesIds
				this.$modal.show("confirmationModal")
			}
		},

		async onConfirm() {
			if (this.shareForm.toggleShare) this.sharePolicies()
			if (this.shareForm.toggleSendNotification) {
				const toEntityId = await this.fetchInternalId(this.createdById)
				this.shareForm.policiesIds.forEach(id =>
					this.sendNotification(id, toEntityId)
				)
			}
			this.$modal.hide("confirmationModal")
		},

		async sharePolicies() {
			// Add targetted permissions to createdBy's readPolicies with policiesIds
			const query = gql`
				mutation addTargettedPermissions(
					$loginId: String!
					$inputs: [addTargettedPermissionInput]!
				) {
					addTargettedPermissions(loginId: $loginId, inputs: $inputs) {
						errors
						status
					}
				}
			`
			const variables = {
				loginId: this.createdById,
				inputs: this.shareForm.policiesIds.map(id => ({
					type: "readPolicies",
					value: id
				}))
			}
			const res = await api({ query, variables })
			this.$modal.hide("confirmationModal")
			// Errors
			if (res?.data?.addTargettedPermissions?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addTargettedPermissions?.errors?.[0] ||
						this.$t("errors.general")
				)
				return
			}
		},

		async sendNotification(policyId, toEntityId) {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			this.isLoading = true

			const query = gql`
				mutation sendNotification($input: sendNotificationInput!) {
					sendNotification(input: $input) {
						status
						errors
					}
				}
			`

			const variables = {
				input: {
					toEntityId,
					internalMessage: {
						dataJsonString: JSON.stringify({
							policy: policyId
						}),
						templateRendering: {
							templateId: this.policyCreatedTemplateId,
							input: {
								name: "data",
								contentJsonString: JSON.stringify({
									holder: await this.fetchPolicyHolder(policyId)
								})
							}
						}
					}
				}
			}

			const res = await api({ query, variables })
			this.isLoading = false

			if (
				res?.data?.errors?.length ||
				res.data?.sendNotification?.errors?.length
			) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.errors?.toString() ||
							res.data?.sendNotification?.errors
					}
				})
			}
		},

		async fetchPolicyCreatedTemplate() {
			const query = gql`
				query fetchTemplates($where: templateWhereInput) {
					templates(where: $where) {
						list {
							id
							name
						}
					}
				}
			`

			const variables = {
				where: {
					and: [
						{
							type: "notification"
						},
						{
							name: "Policy Created"
						}
					]
				}
			}

			const res = await api({ query, variables })

			this.policyCreatedTemplateId = res?.data?.templates?.list?.[0]?.id
		},

		async fetchPolicyHolder(id) {
			const query = gql`
				query policy($where: policyWhereInput) {
					policies(where: $where) {
						list {
							issuerNumber
							holder {
								name
							}
						}
					}
				}
			`
			const res = await api({ query, variables: { where: { id } } })
			return res?.data?.policies?.list?.[0]?.holder?.name
		},

		async fetchInternalId(loginId) {
			// Need to get internal id of the user
			const query = gql`
				query logins($ids: [String]) {
					logins(ids: $ids) {
						list {
							associatedUser {
								id
							}
						}
					}
				}
			`
			const res = await api({ query, variables: { ids: [loginId] } })
			return res?.data?.logins?.list?.[0]?.associatedUser?.id
		}
	},

	created() {
		this.onSelect(this.tabs[0])
		this.fetchPolicyCreatedTemplate()
	}
}
</script>
